import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BarChart3, ChevronRight, Crosshair, Filter, Search, X } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { FunnelStatsDetails, FunnelStatsNode, FunnelStatsPage } from '@/types/funnel';
import { causeMeta } from '@/components/broadcast-details-modal/errorCatalog';
import { fmtDateTime, fmtDec, fmtInt } from '@/components/broadcast-details-modal/utils';
import { MAX_RANGE_DAYS, ymd, type DateRange } from '@/pages/Meta/Flow/Funnel/hooks/useFunnelStats';
import { CompactDateRangePicker } from '@/pages/Dashboard/components/CompactDateRangePicker';
import { INFO, InfoTip } from '../stats/InfoTip';

type Tab = 'nodes' | 'pages' | 'overview';
type NodeSort = 'flow' | 'stuck' | 'failed' | 'ctr' | 'clicks';
type PageSort = 'sent' | 'ctr_lead' | 'clicks' | 'failed';

interface Props {
    details: FunnelStatsDetails | null;
    pages: FunnelStatsPage[] | null;
    loading: boolean;
    error: boolean;
    range: DateRange;
    onRangeChange: (r: DateRange) => void;
    pageFilter: string | null;
    onPageFilter: (pageId: string | null) => void;
    focusedId: number | null;
    onFocusNode: (nodeId: number) => void;
    onClose: () => void;
}

const WIDTH_KEY = 'funnel-stats-panel-width';
const MIN_W = 420;

const NODE_SORTS: { id: NodeSort; label: string }[] = [
    { id: 'flow', label: 'Fluxo' },
    { id: 'stuck', label: 'Parados' },
    { id: 'failed', label: 'Falhas' },
    { id: 'clicks', label: 'Cliques' },
    { id: 'ctr', label: 'CTR' },
];

const PAGE_SORTS: { id: PageSort; label: string }[] = [
    { id: 'sent', label: 'Envios' },
    { id: 'ctr_lead', label: 'CTR' },
    { id: 'clicks', label: 'Cliques' },
    { id: 'failed', label: 'Falhas' },
];

const pageLabel = (p: { page_name: string | null; page_id: string }) => p.page_name ?? 'Página sem cadastro';

function readWidth() {
    try {
        const v = Number(localStorage.getItem(WIDTH_KEY));
        return v >= MIN_W ? v : 560;
    } catch {
        return 560;
    }
}

export default function FunnelStatsPanel(props: Props) {
    const { details, pages, loading, error, range, onRangeChange, pageFilter, onPageFilter, focusedId, onFocusNode, onClose } = props;
    const [tab, setTab] = useState<Tab>('nodes');
    const [width, setWidth] = useState(readWidth);
    const t = details?.totals;
    const allPages = pages ?? [];
    const selectedPage = pageFilter ? allPages.find((p) => p.page_id === pageFilter) : null;

    const startResize = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startW = width;
        const max = Math.round(window.innerWidth * 0.7);
        let last = startW;
        const move = (ev: MouseEvent) => {
            last = Math.min(max, Math.max(MIN_W, startW + (startX - ev.clientX)));
            setWidth(last);
        };
        const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            document.body.style.cursor = '';
            try { localStorage.setItem(WIDTH_KEY, String(last)); } catch { /* sem storage */ }
        };
        document.body.style.cursor = 'col-resize';
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }, [width]);

    return (
        <aside style={{ width }} className="relative shrink-0 h-full border-l border-gray-200 bg-white flex flex-col min-h-0">
            <div
                onMouseDown={startResize}
                className="absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 cursor-col-resize z-20 hover:bg-blue-400/40 active:bg-blue-500/50"
                title="Arraste para redimensionar"
            />

            <div className="flex items-center gap-2 px-4 h-12 border-b border-gray-100 shrink-0">
                <BarChart3 className="w-4 h-4 text-gray-500" />
                <span className="font-semibold text-sm">Desempenho</span>
                <div className="ml-auto">
                    <CompactDateRangePicker startDate={range.start} endDate={range.end} onDateChange={(a, b) => onRangeChange(clampRange(a, b))} />
                </div>
                <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Fechar painel">
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="px-4 py-2 border-b border-gray-100 shrink-0">
                {pageFilter ? (
                    <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-2.5 py-1.5">
                        <Filter className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="text-[12px] font-semibold text-blue-900 truncate">
                                {selectedPage ? pageLabel(selectedPage) : 'Página'}
                            </div>
                            <div className="text-[10px] text-blue-700 font-mono">ID {pageFilter} · tudo abaixo e nos nodes é só desta página</div>
                        </div>
                        <button type="button" onClick={() => onPageFilter(null)} className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-0.5">
                            <X className="w-3.5 h-3.5" />Funil inteiro
                        </button>
                    </div>
                ) : (
                    <PagePicker pages={allPages} onPick={onPageFilter} />
                )}
            </div>

            {error ? (
                <div className="p-6 text-sm text-red-600">Não foi possível carregar o desempenho do funil.</div>
            ) : loading || !details || !t ? (
                <div className="p-4 space-y-3">
                    <div className="h-24 rounded-lg bg-gray-100 animate-pulse" />
                    <div className="h-64 rounded-lg bg-gray-100 animate-pulse" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-4 border-b border-gray-100 shrink-0">
                        <Kpi label="Leads" value={fmtInt(t.leads_reached)} info={INFO.leads} />
                        <Kpi label="Enviados" value={fmtInt(t.sent)} sub={t.leads_reached ? `${fmtDec(t.sent / t.leads_reached)} por lead` : undefined} cls="text-blue-700" info={INFO.enviados} />
                        <Kpi label="Falhas" value={fmtInt(t.failed)} sub={`${fmtDec(100 - t.success_rate)}%`} cls="text-red-600" info={INFO.falhas} />
                        <Kpi label="Parados" value={fmtInt(t.stuck)} sub={t.waiting ? `${fmtInt(t.waiting)} em delay` : `${fmtInt(t.stuck_all)} desde sempre`} cls="text-orange-700" info={INFO.parados} />
                        <Kpi label="Cliques" value={fmtInt(t.clicks)} sub={`de ${fmtInt(t.unique_clickers)} leads`} cls="text-indigo-700" info={INFO.cliques} />
                        <Kpi label="CTR por lead" value={`${fmtDec(t.leads_reached ? (t.unique_clickers / t.leads_reached) * 100 : 0)}%`} sub={`${fmtDec(t.ctr)}% por envio`} cls="text-emerald-700" info={INFO.ctr} />
                        <Kpi label="Sucesso" value={`${fmtDec(t.success_rate)}%`} cls="text-emerald-700" info={INFO.sucesso} />
                        <Kpi label="Páginas" value={pageFilter ? '1' : fmtInt(details.funnel.active_pages)} sub={pageFilter ? 'filtrada' : `de ${fmtInt(details.funnel.total_pages)}`} info={INFO.paginas} />
                    </div>

                    <div className="flex gap-4 px-4 border-b border-gray-100 shrink-0">
                        {([['nodes', `Nodes (${details.nodes.length})`], ['pages', `Páginas (${allPages.length})`], ['overview', 'Visão geral']] as [Tab, string][]).map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setTab(id)}
                                className={`py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                            >
                                <span className="flex items-center gap-1">
                                    {label}
                                    {id === 'nodes' && <InfoTip title="Nodes" text={INFO.abaNodes} />}
                                    {id === 'pages' && <InfoTip title="Páginas" text={INFO.abaPaginas} />}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {tab === 'nodes' && <NodesList details={details} focusedId={focusedId} onFocus={onFocusNode} />}
                        {tab === 'pages' && (
                            <PagesList
                                pages={allPages}
                                topPageId={details.top_page?.page_id ?? null}
                                selected={pageFilter}
                                onSelect={(id) => onPageFilter(id === pageFilter ? null : id)}
                            />
                        )}
                        {tab === 'overview' && (
                            <Overview
                                details={details}
                                onFocus={(id) => { setTab('nodes'); onFocusNode(id); }}
                                onPageFilter={pageFilter ? undefined : onPageFilter}
                            />
                        )}
                    </div>

                    <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100 shrink-0">
                        Enviado = aceito pela API do Meta. CTR por lead = leads que clicaram ÷ leads que chegaram. Parado = lead do período cujo último node é aquele.
                    </div>
                </>
            )}
        </aside>
    );
}

/** O backend aceita até 93 dias e nada depois de hoje: ajusta em vez de devolver erro. */
function clampRange(start: string, end: string): DateRange {
    const today = ymd(new Date());
    const e = end > today ? today : end;
    const min = new Date(`${e}T00:00:00`);
    min.setDate(min.getDate() - (MAX_RANGE_DAYS - 1));
    const s = start < ymd(min) ? ymd(min) : start;
    return { start: s > e ? e : s, end: e };
}

function PagePicker({ pages, onPick }: { pages: FunnelStatsPage[]; onPick: (id: string) => void }) {
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const list = useMemo(() => {
        const term = q.trim().toLowerCase();
        return pages.filter((p) => !term || (p.page_name ?? '').toLowerCase().includes(term) || p.page_id.includes(term)).slice(0, 60);
    }, [pages, q]);

    return (
        <div ref={ref} className="relative">
            <div className="flex items-center gap-2 h-8 rounded-md border border-gray-200 px-2 focus-within:border-blue-500">
                <Search className="w-3.5 h-3.5 text-gray-400" />
                <input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={`Filtrar por página (nome ou ID) · ${pages.length} páginas`}
                    className="flex-1 min-w-0 text-sm outline-none bg-transparent"
                />
                <InfoTip title="Filtro de página" text={INFO.filtroPagina} />
            </div>
            {open && (
                <div className="absolute left-0 right-0 top-9 z-30 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
                    {list.map((p) => (
                        <button
                            key={p.page_id}
                            type="button"
                            onClick={() => { onPick(p.page_id); setOpen(false); setQ(''); }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                        >
                            <span className="min-w-0 flex-1">
                                <span className="block text-[13px] font-medium text-gray-900 truncate">{pageLabel(p)}</span>
                                <span className="block text-[10px] text-gray-400 font-mono">{p.page_id}</span>
                            </span>
                            <span className="text-[11px] text-gray-500 shrink-0">{fmtInt(p.sent)} env. · {fmtDec(p.ctr_lead)}%</span>
                        </button>
                    ))}
                    {!list.length && <div className="px-3 py-4 text-sm text-gray-400 text-center">Nenhuma página</div>}
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, sub, cls = 'text-gray-900', info }: { label: string; value: string; sub?: string; cls?: string; info?: string }) {
    return (
        <div className="px-3 py-2 border-r border-b border-gray-100 [&:nth-child(4n)]:border-r-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1">
                {label}{info && <InfoTip title={label} text={info} />}
            </div>
            <div className={`text-[15px] font-bold leading-tight ${cls}`}>{value}</div>
            {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
        </div>
    );
}

function Seg<T extends string>({ items, value, onChange }: { items: { id: T; label: string }[]; value: T; onChange: (v: T) => void }) {
    return (
        <div className="flex flex-wrap gap-1">
            {items.map((i) => (
                <button
                    key={i.id}
                    type="button"
                    onClick={() => onChange(i.id)}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${value === i.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
                >
                    {i.label}
                </button>
            ))}
        </div>
    );
}

function CtrBar({ ctr, max }: { ctr: number; max: number }) {
    return (
        <span className="flex items-center gap-1.5 w-[100px] shrink-0">
            <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <span className="block h-full bg-emerald-500 rounded-full" style={{ width: `${max ? Math.min(100, (ctr / max) * 100) : 0}%` }} />
            </span>
            <span className="text-[11px] font-semibold text-emerald-700 w-[40px] text-right">{fmtDec(ctr)}%</span>
        </span>
    );
}

function NodesList({ details, focusedId, onFocus }: { details: FunnelStatsDetails; focusedId: number | null; onFocus: (id: number) => void }) {
    const [sort, setSort] = useState<NodeSort>('flow');
    const [onlyActive, setOnlyActive] = useState(false);
    const [q, setQ] = useState('');
    const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const nodes = useMemo(() => {
        const term = q.trim().toLowerCase();
        let list = details.nodes;
        if (term) list = list.filter((n) => (n.nome ?? '').toLowerCase().includes(term) || n.tipo.toLowerCase().includes(term) || n.buttons.some((b) => b.text.toLowerCase().includes(term)));
        if (onlyActive) list = list.filter((n) => n.sent || n.failed || n.clicks || n.stuck || n.waiting);
        if (sort === 'flow') return list;
        const key = (n: FunnelStatsNode) => (sort === 'stuck' ? n.stuck + n.waiting : n[sort]);
        return [...list].sort((a, b) => key(b) - key(a));
    }, [details.nodes, sort, onlyActive, q]);

    const maxCtr = Math.max(0, ...details.nodes.flatMap((n) => [n.ctr, ...n.buttons.map((b) => b.ctr)]));

    useEffect(() => {
        if (focusedId != null) rowRefs.current.get(focusedId)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [focusedId]);

    return (
        <div>
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-4 py-2 border-b border-gray-100 space-y-2">
                <div className="flex items-center gap-2 h-8 rounded-md border border-gray-200 px-2 focus-within:border-blue-500">
                    <Search className="w-3.5 h-3.5 text-gray-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar node, tipo ou texto do botão…" className="flex-1 min-w-0 text-sm outline-none bg-transparent" />
                    {q && <button type="button" onClick={() => setQ('')} className="text-gray-400"><X className="w-3.5 h-3.5" /></button>}
                </div>
                <div className="flex items-center gap-2">
                    <Seg items={NODE_SORTS} value={sort} onChange={setSort} />
                    <label className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer select-none">
                        <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} className="w-3 h-3" />
                        só com atividade
                    </label>
                </div>
            </div>
            {nodes.map((n) => {
                const focused = focusedId === n.node_id;
                const topBtn = details.top_button?.node_id === n.node_id ? details.top_button.button_id : null;
                return (
                    <div
                        key={n.node_id}
                        ref={(el) => { if (el) rowRefs.current.set(n.node_id, el); else rowRefs.current.delete(n.node_id); }}
                        className={`border-b border-gray-100 ${focused ? 'bg-amber-50' : ''}`}
                    >
                        <button type="button" onClick={() => onFocus(n.node_id)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 group">
                            <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-md text-[11px] font-bold flex items-center justify-center shrink-0 ${n.is_trigger ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-600'}`}>
                                    {n.is_trigger ? '⚡' : n.order}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[13px] font-semibold text-gray-900 truncate">{n.nome || `Node ${n.node_id}`}</span>
                                    <span className="block text-[10px] uppercase tracking-wide text-gray-400">{n.tipo}</span>
                                </span>
                                <Crosshair className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 shrink-0" />
                            </div>
                            <div className="mt-1.5 pl-8 flex items-center gap-3 text-[11px] flex-wrap">
                                {n.sent > 0 && <span className="text-blue-700 font-semibold">{fmtInt(n.sent)} env.</span>}
                                {n.failed > 0 && (
                                    <span className="text-red-600 font-semibold" title={n.errors.map((e) => `${fmtInt(e.count)} — ${causeMeta(e.code).label}`).join('\n')}>
                                        {fmtInt(n.failed)} falhas
                                    </span>
                                )}
                                {n.clicks > 0 && <span className="text-emerald-700 font-semibold">{fmtInt(n.clicks)} cliques · {fmtDec(n.ctr)}%</span>}
                                {n.stuck > 0 && <span className="text-orange-700 font-semibold">{fmtInt(n.stuck)} parados</span>}
                                {n.waiting > 0 && <span className="text-amber-700 font-semibold">{fmtInt(n.waiting)} em delay</span>}
                                {!n.sent && !n.failed && !n.clicks && !n.stuck && !n.waiting && <span className="text-gray-400">sem atividade</span>}
                            </div>
                        </button>
                        {n.buttons.length > 0 && (
                            <div className="pl-12 pr-4 pb-2.5 space-y-1">
                                {n.buttons.map((b) => (
                                    <div key={b.button_id} onClick={() => onFocus(n.node_id)} className="flex items-center gap-2 text-[12px] cursor-pointer rounded hover:bg-gray-50">
                                        <span className={`text-[9px] font-bold uppercase rounded px-1 shrink-0 ${b.type === 'deleted' ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-100'}`}>{b.type === 'media' ? 'mídia' : b.type === 'deleted' ? 'excluído' : b.type}</span>
                                        <span className={`truncate flex-1 ${b.type === 'deleted' ? 'text-gray-400 italic' : 'text-gray-700'}`} title={b.type === 'deleted' ? 'Cliques em botões que foram apagados deste node. Continuam no total do node.' : b.text}>{b.text}</span>
                                        {topBtn === b.button_id && <span className="text-[9px] font-bold text-amber-800 bg-amber-100 rounded px-1 shrink-0">TOP</span>}
                                        <span className="text-gray-500 w-[46px] text-right shrink-0">{b.clicks ? fmtInt(b.clicks) : '–'}</span>
                                        {n.sent && b.clicks ? <CtrBar ctr={b.ctr} max={maxCtr} /> : <span className="w-[100px] shrink-0" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
            {!nodes.length && <div className="p-6 text-center text-sm text-gray-400">Nenhum node encontrado</div>}
        </div>
    );
}

function PagesList({ pages, topPageId, selected, onSelect }: { pages: FunnelStatsPage[]; topPageId: string | null; selected: string | null; onSelect: (id: string) => void }) {
    const [sort, setSort] = useState<PageSort>('sent');
    const [q, setQ] = useState('');
    const [onlyErrors, setOnlyErrors] = useState(false);
    const list = useMemo(() => {
        const term = q.trim().toLowerCase();
        return pages
            .filter((p) => (!term || (p.page_name ?? '').toLowerCase().includes(term) || p.page_id.includes(term)) && (!onlyErrors || p.failed > 0))
            .sort((a, b) => b[sort] - a[sort] || b.sent - a.sent);
    }, [pages, sort, q, onlyErrors]);
    const maxCtr = Math.max(0, ...pages.map((p) => p.ctr_lead));

    return (
        <div>
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-4 py-2 border-b border-gray-100 space-y-2">
                <div className="flex items-center gap-2 h-8 rounded-md border border-gray-200 px-2 focus-within:border-blue-500">
                    <Search className="w-3.5 h-3.5 text-gray-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar página ou ID…" className="flex-1 min-w-0 text-sm outline-none bg-transparent" />
                    {q && <button type="button" onClick={() => setQ('')} className="text-gray-400"><X className="w-3.5 h-3.5" /></button>}
                </div>
                <div className="flex items-center gap-2">
                    <Seg items={PAGE_SORTS} value={sort} onChange={setSort} />
                    <label className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer select-none">
                        <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} className="w-3 h-3" />
                        só com falhas
                    </label>
                </div>
                <p className="text-[10px] text-gray-400">Clique numa página para ver só os números dela — no painel e nos nodes.</p>
            </div>
            {list.map((p) => {
                const on = selected === p.page_id;
                return (
                    <button
                        key={p.page_id}
                        type="button"
                        onClick={() => onSelect(p.page_id)}
                        className={`w-full text-left px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 ${on ? 'bg-blue-50 ring-1 ring-inset ring-blue-300' : ''} ${p.in_funnel && p.is_active ? '' : 'opacity-60'}`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1">
                                <span className="block text-[13px] font-semibold text-gray-900 truncate" title={pageLabel(p)}>{pageLabel(p)}</span>
                                <span className="block text-[10px] text-gray-400 font-mono">
                                    {p.page_id}{!p.in_funnel ? ' · fora do funil' : !p.is_active ? ' · inativa no funil' : ''}
                                </span>
                            </span>
                            {p.page_id === topPageId && <span className="text-[9px] font-bold text-amber-800 bg-amber-100 rounded px-1 shrink-0">MAIS CONVERTE</span>}
                            {p.leads_reached > 0 && <CtrBar ctr={p.ctr_lead} max={maxCtr} />}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                            <span><b className="text-gray-800">{fmtInt(p.leads_reached)}</b> leads</span>
                            <span><b className="text-blue-700">{fmtInt(p.sent)}</b> env.</span>
                            {p.failed > 0 && (
                                <span className="flex items-center gap-1" title={p.errors.map((e) => `${fmtInt(e.count)} — ${causeMeta(e.code).label}`).join('\n')}>
                                    {p.errors.slice(0, 3).map((e) => <i key={`${e.code}|${e.subcode}`} className="w-1.5 h-1.5 rounded-sm inline-block" style={{ background: causeMeta(e.code).color }} />)}
                                    <b className="text-red-600">{fmtInt(p.failed)}</b> falhas
                                </span>
                            )}
                            <span><b className="text-gray-800">{fmtInt(p.clicks)}</b> cliques</span>
                            {p.last_send_at && <span className="ml-auto">{fmtDateTime(p.last_send_at)}</span>}
                        </div>
                    </button>
                );
            })}
            {!list.length && <div className="p-6 text-center text-sm text-gray-400">Nenhuma página</div>}
        </div>
    );
}

const dayFmt = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

function DailyChart({ daily }: { daily: FunnelStatsDetails['daily'] }) {
    const data = daily.map((d) => ({ ...d, label: dayFmt(d.date), ctr: d.sent ? Number(((d.clicks / d.sent) * 100).toFixed(1)) : 0 }));
    if (!data.length) return <div className="text-sm text-gray-400">Sem envios no período</div>;
    return (
        <div className="h-56 -ml-2">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#f1f2f4" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} minTickGap={18} />
                    <YAxis yAxisId="n" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={44}
                           tickFormatter={(v: number) => (v >= 1000 ? `${fmtDec(v / 1000, 1)}k` : String(v))} />
                    <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 10, fill: '#059669' }} tickLine={false} axisLine={false} width={34}
                           tickFormatter={(v: number) => `${v}%`} />
                    <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        labelFormatter={(_, payload) => String(payload?.[0]?.payload?.date ?? '').split('-').reverse().join('/')}
                        formatter={(value, name) => (name === 'CTR' ? [`${fmtDec(Number(value))}%`, name] : [fmtInt(Number(value)), name])}
                    />
                    <Line yAxisId="n" type="monotone" dataKey="sent" name="Enviados" stroke="#2563eb" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Line yAxisId="n" type="monotone" dataKey="clicks" name="Cliques" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Line yAxisId="n" type="monotone" dataKey="failed" name="Falhas" stroke="#ef4444" strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} />
                    <Line yAxisId="pct" type="monotone" dataKey="ctr" name="CTR" stroke="#059669" strokeWidth={1.5} strokeDasharray="4 3" dot={false} activeDot={{ r: 3 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function Overview({ details, onFocus, onPageFilter }: { details: FunnelStatsDetails; onFocus: (nodeId: number) => void; onPageFilter?: (pageId: string) => void }) {
    const { top_button: tb, top_page: tp, daily, errors, nodes } = details;
    const mostStuck = nodes.reduce<FunnelStatsNode | null>((a, n) => (n.stuck + n.waiting > (a ? a.stuck + a.waiting : 0) ? n : a), null);
    const mostFailed = nodes.reduce<FunnelStatsNode | null>((a, n) => (n.failed > (a?.failed ?? 0) ? n : a), null);
    const totalErr = errors.reduce((a, e) => a + e.count, 0);

    return (
        <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
                <Highlight
                    label="Botão mais clicado"
                    info={INFO.botaoTop}
                    title={tb ? tb.text : 'Nenhum clique no período'}
                    sub={tb ? `no node “${tb.node_nome}” · ${fmtInt(tb.clicks)} cliques · CTR ${fmtDec(tb.ctr)}%` : undefined}
                    onClick={tb ? () => onFocus(tb.node_id) : undefined}
                />
                <Highlight
                    label="Onde mais leads param"
                    info={INFO.maisParam}
                    title={mostStuck ? mostStuck.nome || `Node ${mostStuck.node_id}` : 'Nenhum lead parado'}
                    sub={mostStuck ? `${fmtInt(mostStuck.stuck)} parados${mostStuck.waiting ? ` · ${fmtInt(mostStuck.waiting)} em delay` : ''}` : undefined}
                    tone="text-orange-700"
                    onClick={mostStuck ? () => onFocus(mostStuck.node_id) : undefined}
                />
                {mostFailed && (
                    <Highlight
                        label="Node com mais falhas"
                        info={INFO.maisFalhas}
                        title={mostFailed.nome || `Node ${mostFailed.node_id}`}
                        sub={[`${fmtInt(mostFailed.failed)} falhas`, mostFailed.errors.slice(0, 2).map((e) => causeMeta(e.code).label).join(', ')].filter(Boolean).join(' · ')}
                        tone="text-red-600"
                        onClick={() => onFocus(mostFailed.node_id)}
                    />
                )}
                <Highlight
                    label="Página que mais converte"
                    info={INFO.paginaTop}
                    title={tp ? pageLabel(tp) : `Nenhuma página com ${details.min_leads_for_ranking}+ leads`}
                    sub={tp ? `ID ${tp.page_id} · CTR ${fmtDec(tp.ctr_lead)}% por lead (${fmtInt(tp.unique_clickers)} de ${fmtInt(tp.leads_reached)})` : undefined}
                    tone="text-emerald-700"
                    onClick={tp && onPageFilter ? () => onPageFilter(tp.page_id) : undefined}
                    action="filtrar"
                />
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1">Por dia <InfoTip title="Por dia" text={INFO.grafico} /></span>
                    <Legend color="#2563eb" label="Enviados" />
                    <Legend color="#8b5cf6" label="Cliques" />
                    <Legend color="#ef4444" label="Falhas" />
                    <Legend color="#059669" label="CTR (eixo direito)" dashed />
                </div>
                <DailyChart daily={daily} />
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
                    Falhas por causa · {fmtInt(totalErr)} <InfoTip title="Falhas por causa" text={INFO.falhasCausa} />
                </div>
                {errors.length ? errors.map((e) => {
                    const m = causeMeta(e.code);
                    return (
                        <div key={`${e.code}|${e.subcode}`} className="py-1" title={m.description}>
                            <div className="flex items-center gap-2 text-[12px]">
                                <i className="w-2 h-2 rounded-sm shrink-0" style={{ background: m.color }} />
                                <span className="truncate flex-1">{m.label}{e.subcode ? <span className="text-gray-400"> · {e.subcode}</span> : null}</span>
                                <span className="font-semibold">{fmtInt(e.count)}</span>
                                <span className="text-gray-400 w-12 text-right">{fmtDec((e.count / totalErr) * 100)}%</span>
                            </div>
                            <div className="mt-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                                <span className="block h-full" style={{ width: `${(e.count / totalErr) * 100}%`, background: m.color }} />
                            </div>
                        </div>
                    );
                }) : <div className="text-sm text-gray-400">Nenhuma falha</div>}
            </div>
        </div>
    );
}

function Legend({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
    return (
        <span className="flex items-center gap-1 text-[11px] text-gray-500">
            <span className="w-3.5 h-0 inline-block" style={{ borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }} />
            {label}
        </span>
    );
}

function Highlight({ label, title, sub, tone = 'text-gray-900', onClick, action, info }: { label: string; title: string; sub?: string; tone?: string; onClick?: () => void; action?: string; info?: string }) {
    const body: ReactNode = (
        <>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1">
                {label}{info && <InfoTip title={label} text={info} />}
            </div>
            <div className={`text-[13px] font-bold truncate ${tone}`} title={title}>{title}</div>
            {sub && <div className="text-[11px] text-gray-500 line-clamp-2">{sub}</div>}
        </>
    );
    return onClick ? (
        <button type="button" onClick={onClick} className="text-left rounded-lg border border-gray-200 px-3 py-2 hover:border-amber-400 hover:bg-amber-50/40 flex items-center gap-2 min-w-0">
            <span className="min-w-0 flex-1">{body}</span>
            {action ? <span className="text-[10px] font-semibold text-blue-600 shrink-0">{action}</span> : <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
        </button>
    ) : (
        <div className="rounded-lg border border-gray-200 px-3 py-2 min-w-0">{body}</div>
    );
}
